import { IconType } from 'react-icons';
import {
  FiUsers,
  FiUserPlus,
  FiTarget,
  FiCheckSquare,
  FiSettings,
  FiDatabase,
  FiList,
  FiGitBranch,
  FiSearch,
  FiTool,
  FiLayout,
  FiMessageCircle,
  FiSave,
} from 'react-icons/fi';
import { WorkspaceUrlSectionKind } from './workspaceUrl';

/**
 * `kind` identifies the URL target for `buildUrl` / `buildWorkspaceUrl`
 * (restricted to no-param section kinds — detail pages don't appear here).
 * `path` is the trailing URL segment for that target — kept around so the
 * Header can do reverse-lookup (pathname → route label) without rebuilding
 * the inverse mapping.
 */
export interface SiteRoute {
  kind: WorkspaceUrlSectionKind;
  path: string;
  label: string;
  icon: IconType;
  children?: SiteRoute[];
  showInMenu?: boolean;
}

export interface NavigationGroup {
  id: string;
  label: string;
  icon: IconType;
  routes: SiteRoute[];
}

// ─── Top-level routes (function-based dashboards) ───────────────────
export const topLevelRoutes: SiteRoute[] = [
  { kind: 'dashboard', path: 'dashboard', label: 'Dashboard', icon: FiLayout, showInMenu: true },
  { kind: 'work', path: 'work', label: 'Your Work', icon: FiCheckSquare, showInMenu: true },
  { kind: 'teams', path: 'teams', label: 'Your Teams', icon: FiUsers, showInMenu: true },
  { kind: 'interests', path: 'interests', label: 'Your Interests', icon: FiTarget, showInMenu: true },
];

// ─── Footer route (rendered separately, just above the user profile) ─
export const assistantRoute: SiteRoute = {
  kind: 'assistant',
  path: 'assistant',
  label: 'Assistant',
  icon: FiMessageCircle,
  showInMenu: true,
};

// ─── Navigation groups ──────────────────────────────────────────────
export const navigationGroups: NavigationGroup[] = [
  {
    id: 'data',
    label: 'Data',
    icon: FiSearch,
    routes: [
      { kind: 'data-explore', path: 'data/explore', label: 'Explore', icon: FiSearch, showInMenu: true },
      { kind: 'workflows', path: 'data/workflows', label: 'Workflows', icon: FiGitBranch, showInMenu: true },
    ],
  },
  {
    id: 'workspace',
    label: 'Workspace',
    icon: FiSettings,
    routes: [
      { kind: 'settings', path: 'workspace/settings', label: 'Settings', icon: FiSettings, showInMenu: true },
      { kind: 'data-sources', path: 'workspace/data-sources', label: 'Data Sources', icon: FiDatabase, showInMenu: true },
      { kind: 'invitations', path: 'workspace/invitations', label: 'Invitations', icon: FiUserPlus, showInMenu: true },
      { kind: 'pending-changes', path: 'workspace/pending-changes', label: 'Pending Changes', icon: FiSave, showInMenu: true },
      { kind: 'history', path: 'workspace/history', label: 'History', icon: FiList, showInMenu: true },
      { kind: 'tools', path: 'workspace/tools', label: 'Tools', icon: FiTool, showInMenu: true },
    ],
  },
];

// ─── All routes (flat list for lookups) ─────────────────────────────
export const siteRoutes: SiteRoute[] = [
  ...topLevelRoutes,
  assistantRoute,
  ...navigationGroups.flatMap(g => g.routes),
];

// Utility function to get a route by path
export const getRouteByPath = (path: string): SiteRoute | undefined => {
  return siteRoutes.find(route => route.path === path);
};
